import React, { useState, useEffect, useMemo } from "react";
import { Global } from "../helpers/Global";
import useAuth from "../hooks/useAuth";
import Select from "react-select";

export const MisJornadasFinalizadas = () => {
  const { auth } = useAuth();
  const [jornadas, setJornadas] = useState([]);
  const [centros, setCentros] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [filtroCliente, setFiltroCliente] = useState("");
  const [fechaDesde, setFechaDesde] = useState("");
  const [fechaHasta, setFechaHasta] = useState("");
  const [filtroMes, setFiltroMes] = useState("");
  const [filtroAnio, setFiltroAnio] = useState("");
  const [busquedaTexto, setBusquedaTexto] = useState("");
  const [cargando, setCargando] = useState(true);
  const [mensaje, setMensaje] = useState("");
  const [orden, setOrden] = useState({ campo: "inicio", ascendente: true });

  useEffect(() => {
    const fetchDatos = async () => {
      try {
        const [resJ, resU, resCentros] = await Promise.all([
          fetch(`${Global.url}jornadas/finalizadas/mias`, {
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
          }),
          fetch(`${Global.url}usuarios`, {
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
          }),
          fetch(`${Global.url}centros/todos`, {
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
          }),
        ]);

        if (resJ.ok && resU.ok && resCentros.ok) {
          const [dataJornadas, dataUsuarios, dataCentros] = await Promise.all([
            resJ.json(),
            resU.json(),
            resCentros.json(),
          ]);

          setUsuarios(dataUsuarios);
          setCentros(dataCentros);

          const normalizadas = dataJornadas.map((j) => {
            const admin = dataUsuarios.find((u) => u.id === j.creado_por_admin_id);
            return {
              ...j,
              duracionMs: new Date(j.fin) - new Date(j.inicio),
              modificado_por_nombre: admin ? `${admin.nombre} ${admin.apellidos || ""}` : null,
            };
          });

          setJornadas(normalizadas);
        } else {
          setMensaje("Error al obtener datos.");
        }
      } catch (error) {
        console.error("Error en la conexión:", error);
        setMensaje("No se pudo conectar con el servidor.");
      } finally {
        setCargando(false);
      }
    };

    fetchDatos();
  }, []);

  const mapaCentros = useMemo(() => {
    const map = {};
    centros.forEach((c) => {
      map[c.id] = c.nombre;
    });
    return map;
  }, [centros]);

  // Normalizador para búsqueda tolerante a acentos/mayúsculas
  const normaliza = (s) =>
    (s ?? "")
      .toString()
      .toLowerCase()
      .normalize("NFD")
      .replace(/\p{Diacritic}/gu, "");

  const q = normaliza(busquedaTexto);

  const jornadasFiltradas = jornadas.filter((j) => {
    const fechaJornada = new Date(j.inicio);
    const anio = fechaJornada.getFullYear().toString();
    const mes = String(fechaJornada.getMonth() + 1).padStart(2, "0");

    const coincideUsuario = j.usuario_id === auth.id;
    const coincideCentro = !filtroCliente || Number(j.centro_id) === Number(filtroCliente);
    const desdeValida = !fechaDesde || fechaJornada >= new Date(fechaDesde);
    const hastaValida =
      !fechaHasta || fechaJornada < new Date(new Date(fechaHasta).setDate(new Date(fechaHasta).getDate() + 1));
    const coincideAnio = !filtroAnio || anio === filtroAnio;
    const coincideMes = !filtroMes || mes === filtroMes;

    // 🔎 Búsqueda en centro, motivo (observaciones + motivo_fin) y modificado_por_nombre
    const coincideTexto =
      !q ||
      normaliza(mapaCentros[j.centro_id] || "").includes(q) ||
      normaliza(j.observaciones_admin).includes(q) ||
      normaliza(j.motivo_fin).includes(q) ||
      normaliza(j.modificado_por_nombre).includes(q);

    return coincideUsuario && coincideCentro && desdeValida && hastaValida && coincideAnio && coincideMes && coincideTexto;
  });

  const jornadasOrdenadas = [...jornadasFiltradas].sort((a, b) => {
    let valorA = a[orden.campo];
    let valorB = b[orden.campo];

    if (orden.campo === "centro") {
      valorA = mapaCentros[a.centro_id] || "";
      valorB = mapaCentros[b.centro_id] || "";
    }

    if (orden.campo === "inicio" || orden.campo === "fin") {
      valorA = new Date(valorA);
      valorB = new Date(valorB);
    }

    if (typeof valorA === "string") valorA = valorA.toLowerCase();
    if (typeof valorB === "string") valorB = valorB.toLowerCase();

    valorA = valorA ?? "";
    valorB = valorB ?? "";

    if (valorA < valorB) return orden.ascendente ? -1 : 1;
    if (valorA > valorB) return orden.ascendente ? 1 : -1;
    return 0;
  });

  const manejarOrden = (campo) => {
    const campoOrden = campo === "duracion" ? "duracionMs" : campo;
    setOrden((prev) => ({
      campo: campoOrden,
      ascendente: prev.campo === campoOrden ? !prev.ascendente : true,
    }));
  };

  const calcularDuracion = (inicio, fin) => {
    if (!inicio || !fin) return "No disponible";
    const fechaInicio = new Date(inicio);
    const fechaFin = new Date(fin);
    if (isNaN(fechaInicio) || isNaN(fechaFin)) return "No disponible";
    const diferenciaMs = fechaFin - fechaInicio;
    const horas = Math.floor(diferenciaMs / 3600000);
    const minutos = Math.floor((diferenciaMs % 3600000) / 60000);
    return `${horas}h ${minutos}min`;
  };

  const totalMs = jornadasFiltradas.reduce((acc, j) => acc + j.duracionMs, 0);
  const formatearDuracion = (ms) => {
    const h = Math.floor(ms / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    return `${h}h ${m}min`;
  };

  // =======================
  // Exportación a PDF
  // =======================
  const calcularRango = () => {
    if (fechaDesde && fechaHasta) return { from: fechaDesde, to: fechaHasta };

    const hoy = new Date();
    const y = filtroAnio ? Number(filtroAnio) : hoy.getFullYear();

    if (filtroMes) {
      const m = Number(filtroMes) - 1; // 0..11
      const from = new Date(y, m, 1);
      const to = new Date(y, m + 1, 0);
      const fmt = (d) => d.toISOString().slice(0, 10);
      return { from: fmt(from), to: fmt(to) };
    }

    if (filtroAnio) {
      const from = new Date(y, 0, 1);
      const to = new Date(y, 11, 31);
      const fmt = (d) => d.toISOString().slice(0, 10);
      return { from: fmt(from), to: fmt(to) };
    }

    if (jornadasFiltradas.length > 0) {
      const minMs = Math.min(...jornadasFiltradas.map((j) => new Date(j.inicio).getTime()));
      const maxMs = Math.max(...jornadasFiltradas.map((j) => new Date(j.fin).getTime()));
      const fmt = (ms) => new Date(ms).toISOString().slice(0, 10);
      return { from: fmt(minMs), to: fmt(maxMs) };
    }

    return null;
  };

  const handleExportPDF = async () => {
    try {
      const rango = calcularRango();
      if (!rango) {
        alert("Indica al menos un rango de fechas (Desde/Hasta) o selecciona Mes/Año.");
        return;
      }

      const params = new URLSearchParams();
      params.set("from", rango.from);
      params.set("to", rango.to);
      if (filtroCliente) params.set("centro_id", String(filtroCliente));
      if (busquedaTexto) params.set("query", busquedaTexto.trim());
      if (filtroMes) params.set("mes", filtroMes);
      if (filtroAnio) params.set("anio", filtroAnio);

      const url = `${Global.url}jornadas/finalizadas/mias/export.pdf?${params.toString()}`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });

      if (!res.ok) {
        const msg = await res.text();
        throw new Error(msg || "No se pudo generar el PDF");
        }

      const blob = await res.blob();
      const nombreCentro = filtroCliente ? (mapaCentros[Number(filtroCliente)] || `centro-${filtroCliente}`) : "todos";
      const fileName = `parte-jornadas-${auth?.id || "me"}-${nombreCentro}-${rango.from}_${rango.to}.pdf`;

      const urlBlob = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = urlBlob;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(urlBlob);
    } catch (e) {
      console.error(e);
      alert("Error al generar el PDF.");
    }
  };

  return (
    <div className="consulta-ticajes">
      <h2 className="centrar-titulo">Mis Jornadas Finalizadas</h2>

      {mensaje && <p className="rojo">{mensaje}</p>}
      {cargando && <p>Cargando jornadas...</p>}

      {/* Filtros */}
      <div className="filtros">
        {/* Centro */}
        <div className="filtro">
          <label>Centro:</label>
          <Select
            options={[
              { value: "", label: "Todos" },
              ...centros
                .map((c) => ({ value: c.id, label: c.nombre }))
                .sort((a, b) => a.label.localeCompare(b.label)),
            ]}
            value={
              filtroCliente
                ? {
                    value: filtroCliente,
                    label: mapaCentros[Number(filtroCliente)] || `Centro ${filtroCliente}`,
                  }
                : { value: "", label: "Todos" }
            }
            onChange={(selected) => setFiltroCliente(selected?.value || "")}
            isClearable
            placeholder="Filtrar por centro"
            className="select-centro"
            classNamePrefix="select"
          />
        </div>

        {/* Desde */}
        <div className="filtro">
          <label>Desde:</label>
          <input
            type="date"
            value={fechaDesde}
            onChange={(e) => setFechaDesde(e.target.value)}
          />
        </div>

        {/* Hasta */}
        <div className="filtro">
          <label>Hasta:</label>
          <input
            type="date"
            value={fechaHasta}
            onChange={(e) => setFechaHasta(e.target.value)}
          />
        </div>

        {/* Año */}
        <div className="filtro">
          <label>Año:</label>
          <Select
            options={[
              { value: "", label: "Todos" },
              ...Array.from({ length: 16 }, (_, i) => {
                const year = 2025 + i;
                return { value: String(year), label: String(year) };
              }),
            ]}
            value={
              filtroAnio
                ? { value: filtroAnio, label: filtroAnio }
                : { value: "", label: "Todos" }
            }
            onChange={(selected) => setFiltroAnio(selected?.value || "")}
            isClearable
            placeholder="Filtrar por año"
            className="select-anio"
            classNamePrefix="select"
          />
        </div>

        {/* Mes */}
        <div className="filtro">
          <label>Mes:</label>
          <select
            value={filtroMes}
            onChange={(e) => setFiltroMes(e.target.value)}
          >
            <option value="">Todos</option>
            {Array.from({ length: 12 }, (_, i) => (
              <option key={i + 1} value={String(i + 1).padStart(2, "0")}>
                {new Date(0, i).toLocaleString("es-ES", { month: "long" })}
              </option>
            ))}
          </select>
        </div>

        {/* Buscar */}
        <div className="filtro">
          <label>Buscar:</label>
          <input
            type="text"
            value={busquedaTexto}
            onChange={(e) => setBusquedaTexto(e.target.value)}
            placeholder="Centro, motivo o modificado por"
          />
        </div>
      </div>

      {/* Tabla */}
      <div className="table-listado-padre">
        <table className="table-listado">
          <thead>
            <tr>
              <th onClick={() => manejarOrden("centro")}>Centro</th>
              <th onClick={() => manejarOrden("inicio")}>Inicio</th>
              <th onClick={() => manejarOrden("fin")}>Fin</th>
              <th onClick={() => manejarOrden("duracion")}>Duración</th>
              <th onClick={() => manejarOrden("modificado_por_nombre")}>
                Modificado por
              </th>
              <th onClick={() => manejarOrden("observaciones_admin")}>Motivo</th>
            </tr>
          </thead>
          <tbody>
            {jornadasOrdenadas.length === 0 ? (
              <tr>
                <td colSpan="6">
                  No hay jornadas finalizadas que coincidan con el filtro.
                </td>
              </tr>
            ) : (
              jornadasOrdenadas.map((j) => (
                <tr key={j.id}>
                  <td>{mapaCentros[j.centro_id] || `Centro ${j.centro_id}`}</td>
                  <td>{new Date(j.inicio).toLocaleString()}</td>
                  <td>{new Date(j.fin).toLocaleString()}</td>
                  <td>{calcularDuracion(j.inicio, j.fin)}</td>
                  <td>{j.modificado_por_nombre || "-"}</td>
                  <td>{j.observaciones_admin || "-"}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        <div
          style={{
            textAlign: "right",
            marginTop: "10px",
            marginRight: "10px",
          }}
        >
          <strong>Total trabajado en este listado:</strong>{" "}
          {formatearDuracion(totalMs)}
        </div>
      </div>

      {/* Botón de exportación PDF con layout unificado */}
      <div className="exportar-botones-padre">
        <div className="exportar-botones">
          <button
            className="btn-mapa"
            onClick={handleExportPDF}
            disabled={cargando || jornadasFiltradas.length === 0}
            title={
              jornadasFiltradas.length === 0
                ? "No hay filas que exportar con los filtros actuales"
                : "Exportar parte (PDF)"
            }
          >
            Exportar parte (PDF)
          </button>
        </div>
      </div>
    </div>
  );
};
